import { put, call, select, spawn, delay } from 'redux-saga/effects';
import { findKey } from 'lodash';
import * as Sentry from '@sentry/nextjs';
import { getUserCookies } from 'reducers/user';
import api from '../utils/api';
import { getCurrentCircuitRound, getCurrentSessionExercise } from '../reducers/exercises';
import { getWorkoutSessionId } from '../reducers/workout';
import {
  receiveFetchSessionExercisesList,
  failureFetchSessionExercisesList,
  receiveCompleteExercise,
  receiveFetchExerciseById,
  requestNextExercise,
} from '../actions/exercise';
import { recieveStartExercise, receiveFetchWorkoutSessions } from '../actions/workout';
import { fetchErrorHandler } from '../actions/fetchErrorHandler';

const getCircuitRound = (state) => {
  return getCurrentCircuitRound(state.exercises);
};

const getSessionExercise = (state) => {
  return getCurrentSessionExercise(state.exercises);
};

export const getStartExerciseBody = (state) => {
  const sessionExercise = getSessionExercise(state);
  if (!sessionExercise) {
    return null;
  }
  return {
    workoutSessionId: getWorkoutSessionId(state),
    exerciseId: sessionExercise.exercise.id,
    sessionExerciseId: sessionExercise.id,
    phase: sessionExercise.phase,
    round: getCircuitRound(state),
  };
};

export const getWorkoutExerciseIdHelper = (state) => {
  const sessionExercise = getSessionExercise(state);
  const round = getCircuitRound(state);

  const { items } = state.workout.exercises || {};

  return findKey(items, (w) => {
    return w && w.sessionExerciseId === sessionExercise?.id && w.round === round;
  });
};

/**
 * Fetch session exercises list.
 */
export function* fetchSessionExercisesList(action) {
  try {
    const { sessionId, timeLength } = action.payload || {};
    const accessToken = yield select(getUserCookies);

    const { data } = yield call(
      api(accessToken).get,
      `/sessions/${sessionId}/session-exercises?timeOption=${timeLength}`,
    );

    yield put(receiveFetchSessionExercisesList({ timeLength, sessionExercises: data, sessionId }));
  } catch (error) {
    yield put(failureFetchSessionExercisesList('Something went wrong'));
    yield put(
      fetchErrorHandler(
        error.response,
        'Oops, we hit a snag fetching your exercises. Try refreshing the page',
      ),
    );
  }
}

const COMPLETE_SYNC_MAX_ATTEMPTS = 4;
const COMPLETE_SYNC_RETRY_BASE_MS = 2000;
const START_MAX_ATTEMPTS = 3;
const START_RETRY_BASE_MS = 1000;
const WAIT_FOR_START_ATTEMPTS = 5;
const WAIT_FOR_START_DELAY_MS = 500;

// The axios response interceptor rejects with the response body, so an HTTP
// status arrives as `statusCode` (`error.response` is never set). Network
// failures (status 0, e.g. a dropped connection on a phone) carry no status.
const getErrorStatus = (error) => error?.statusCode || error?.response?.status;

// complete exercise
export function* completeExercise() {
  let workoutExerciseId = yield select(getWorkoutExerciseIdHelper);

  // Right after an advance, the new exercise's start POST may still be in
  // flight (fast Done/next-arrow taps land here) — give it a moment to
  // register before falling back to creating it here.
  for (let waited = 0; !workoutExerciseId && waited < WAIT_FOR_START_ATTEMPTS; waited += 1) {
    yield delay(WAIT_FOR_START_DELAY_MS);
    workoutExerciseId = yield select(getWorkoutExerciseIdHelper);
  }

  let startBody = null;
  if (!workoutExerciseId) {
    // Captured before advancing, while the current exercise is still selected.
    startBody = yield select(getStartExerciseBody);
    Sentry.captureMessage('Complete exercise: workout exercise id is not defined', 'warning');
  }

  // Advance the workout immediately and persist in the background — a slow or
  // failed request must not stall the player between exercises.
  yield put(receiveCompleteExercise({}));
  yield put(requestNextExercise());
  if (workoutExerciseId) {
    yield spawn(syncCompleteExercise, workoutExerciseId);
  } else if (startBody?.workoutSessionId) {
    yield spawn(startAndSyncCompleteExercise, startBody);
  }
}

/**
 * Create the workout exercise row, retrying network and server failures. The
 * endpoint returns the existing row for a repeated request, so retries are safe.
 */
export function* postStartExercise(body) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      const { data } = yield call(api().post, `/users/workout/exercise`, body);
      yield put(recieveStartExercise({ ...data, sessionExercise: { id: body.sessionExerciseId } }));
      return data.id;
    } catch (error) {
      const status = getErrorStatus(error);
      if ((status && status < 500) || attempt >= START_MAX_ATTEMPTS) {
        throw error;
      }
      yield delay(START_RETRY_BASE_MS * 2 ** (attempt - 1));
    }
  }
}

/**
 * Persist a completion for an exercise whose start never registered. Detached
 * from the player flow like syncCompleteExercise.
 */
export function* startAndSyncCompleteExercise(body) {
  let workoutExerciseId;
  try {
    workoutExerciseId = yield call(postStartExercise, body);
  } catch (error) {
    Sentry.captureException('Unable to start user exercise', { extra: error });
    yield put(fetchErrorHandler(error.response, 'Unable to save your exercise progress'));
    return;
  }
  yield call(syncCompleteExercise, workoutExerciseId);
}

/**
 * Persist an exercise completion with retries. Detached from the player flow:
 * the workout has already advanced by the time this runs, so failures never
 * block the user — they are reported to Sentry via fetchErrorHandler only.
 */
export function* syncCompleteExercise(workoutExerciseId) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      yield call(api().put, `/users/workout/exercise/${workoutExerciseId}`, {
        completed: true,
      });
      return;
    } catch (error) {
      const status = getErrorStatus(error);

      if (status === 404) {
        // The workout exercise row no longer exists (e.g. the session was
        // completed elsewhere) — there is nothing left to persist.
        return;
      }

      if (attempt >= COMPLETE_SYNC_MAX_ATTEMPTS) {
        yield put(fetchErrorHandler(error.response, 'Unable to save your exercise progress'));
        return;
      }

      yield delay(COMPLETE_SYNC_RETRY_BASE_MS * 2 ** (attempt - 1));
    }
  }
}

/**
 * Load the user's unfinished workout for a session into the store, which also
 * sets the current workout id when it is missing.
 */
export function* recoverWorkoutSession(sessionId) {
  const { data } = yield call(
    api().get,
    `users/workout/session?session_id=${sessionId}&completed=false`,
  );
  if (data?.sessions?.length) {
    yield put(receiveFetchWorkoutSessions(data));
  }
}

// start exercise
export function* startExercise(action) {
  const { sessionId } = action.payload;

  if (!sessionId) {
    throw Error('No session Id!');
  }

  const workoutExerciseId = yield select(getWorkoutExerciseIdHelper);

  if (!workoutExerciseId) {
    // If `workoutExerciseId` is not defined, it means that the
    // session exercise hasn't been started yet, and we start it here.
    // A failure here doesn't block the user: completeExercise creates the
    // row itself when it finds none.
    try {
      let body = yield select(getStartExerciseBody);
      if (body && !body.workoutSessionId) {
        // The player can mount without a workout id (e.g. restored state that lost
        // it); the API rejects the start with a 400 then. Recover the in-progress
        // workout for this session before posting.
        yield call(recoverWorkoutSession, sessionId);
        body = yield select(getStartExerciseBody);
      }
      if (body?.workoutSessionId) {
        Sentry.setContext('start workout exercise data', body);
        yield call(postStartExercise, body);
      } else if (body) {
        Sentry.captureMessage('Start exercise: no workout session to attach to', 'warning');
      }
    } catch (error) {
      Sentry.captureException('Unable to start user exercise', { extra: error });
    }
  }
}

// fetch exercise by id
export function* fetchExerciseById(action) {
  try {
    const { exerciseId } = action.payload || {};

    const accessToken = yield select(getUserCookies);

    const { data } = yield call(api(accessToken).get, `/exercise/${exerciseId}`);

    yield put(receiveFetchExerciseById(data));
  } catch (error) {
    yield put(
      fetchErrorHandler(
        error.response,
        'Oops, we hit a snag fetching your exercise. Try refreshing the page',
      ),
    );
  }
}
